/**
 * HeaderChatButton — botão no header que abre o drawer de mensagens.
 *
 * Seletor entre dois mundos:
 *  - "Profissionais" → chat web ↔ aplicativo (threads kind=mobile)
 *  - "Equipe"        → chat interno web ↔ web (threads kind=internal), com @menção
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle,
  X,
  Search,
  Plus,
  Users,
  Smartphone,
  ExternalLink,
  Inbox,
  AtSign,
  Wrench,
  LayoutList,
  Archive,
  ArchiveRestore,
  Pin,
  PinOff,
  Bell,
  BellOff,
  MailOpen,
  Mail,
} from "lucide-react";
import api, { formatApiError } from "../utils/api";
import { toast } from "./ui/sonner";
import IconTip from "./IconTip";
import { subscribeAdminWS } from "../lib/adminWS";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { accentBg } from "../utils/accent";
import NewConversationModal from "./chat/NewConversationModal";
import EditGroupDrawer from "./chat/EditGroupDrawer";
import { groupColors, groupIcon } from "./chat/groupIcons";
import ChatPanel from "./ChatPanel";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import ConversationItem, {
  conversationTitle,
  conversationSubtitle,
  isClosedThread,
  threadIcon,
  ThreadAvatar,
} from "./chat/ConversationItem";
import { STATUS_OPERACIONAL_MAP } from "../pages/services/helpers";
import { useProfPhotos } from "../utils/profPhotos";


// Import dinâmico: evita ciclo com DashboardLayout e só carrega quando usado.

export default function HeaderChatButton() {
  const { user } = useAuth();
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("mobile"); // mobile | internal
  const [threads, setThreads] = useState([]);
  const [internalThreads, setInternalThreads] = useState([]);
  const threadsRef = useRef([]);
  const internalRef = useRef([]);
  const [webUsers, setWebUsers] = useState([]);
  const [mobileUsers, setMobileUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [editGroupId, setEditGroupId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | general | service
  const [search, setSearch] = useState("");


  const [prefs, setPrefs] = useState({});
  const [newOpen, setNewOpen] = useState(false);

  // Deep-link da Central de Notificações: `?chat_thread=<id>` abre a gaveta
  // já nessa conversa (usado pelas mensagens sem serviço vinculado). Reage a
  // troca de query porque o operador normalmente já está na mesma tela.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const tid = new URLSearchParams(location.search).get("chat_thread");
    if (!tid) return;
    setMode("mobile");
    setOpen(true);
    setActiveId(tid);
    const resto = new URLSearchParams(location.search);
    resto.delete("chat_thread");
    navigate(
      { pathname: location.pathname, search: resto.toString() ? `?${resto}` : "" },
      { replace: true },
    );
  }, [location.search, location.key, location.pathname, navigate]);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/threads");
      const items = data?.items || [];
      setThreads(items);
    } catch {
      /* silencioso */
    }
  }, []);

  const fetchInternal = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/internal/threads");
      setInternalThreads(data?.items || []);
    } catch {
      /* silencioso */
    }
  }, []);

  const fetchWebUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/internal/users");
      setWebUsers(data?.items || []);
    } catch {
      /* silencioso */
    }
    try {
      const { data } = await api.get("/chat/mobile-users");
      setMobileUsers(data?.items || []);
    } catch {
      /* silencioso */
    }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/prefs");
      const map = {};
      for (const p of data?.items || []) map[p.thread_id] = p;
      setPrefs(map);
    } catch {
      /* silencioso */
    }
  }, []);

  const setPref = useCallback(
    async (threadId, patch) => {
      setPrefs((prev) => ({
        ...prev,
        [threadId]: { ...(prev[threadId] || {}), ...patch },
      }));
      try {
        await api.post(`/chat/prefs/${threadId}`, patch);
      } catch {
        fetchPrefs();
      }
    },
    [fetchPrefs],
  );

  useEffect(() => {
    fetchThreads();
    fetchInternal();
  }, [fetchThreads, fetchInternal]);

  useEffect(() => {
    if (!open) return;
    // Aquece o chunk do drawer de serviço para a abertura ser instantânea.
    import("../pages/ServicesPage").catch(() => {});
    fetchThreads();
    fetchInternal();
    fetchWebUsers();
    fetchPrefs();
  }, [open, fetchThreads, fetchInternal, fetchWebUsers, fetchPrefs]);

  // Atualização PONTUAL: o evento já traz a mensagem, então mexemos só na
  // linha que mudou. Antes toda mensagem recebida recarregava a lista inteira
  // (até 200 conversas) em cada aba de cada operador — era o pico de CPU.
  const aplicarMensagem = useCallback((payload, interna) => {
    const tid = payload?.thread_id;
    if (!tid) return;
    const msg = payload.message || {};
    const setter = interna ? setInternalThreads : setThreads;
    // A presença é checada na ref (o estado do React só chega no próximo
    // render — ler um flag escrito dentro do updater daria sempre "não achou").
    const listaAtual = interna ? internalRef.current : threadsRef.current;
    const achou = listaAtual.some((t) => t.id === tid);
    setter((prev) => {
      const out = prev.map((t) => {
        if (t.id !== tid) return t;
        const daCentral = (msg.sender_type || "") === "admin";
        const campoNaoLidas = interna ? "unread" : "unread_admin";
        return {
          ...t,
          last_message_at: msg.created_at || t.last_message_at,
          last_message_preview: msg.body || t.last_message_preview,
          last_message_sender_type: msg.sender_type || t.last_message_sender_type,
          ...(msg.sender_type === "admin" || msg.sender_type === "mobile"
            ? { last_human_preview: msg.body, last_human_at: msg.created_at }
            : {}),
          [campoNaoLidas]: daCentral ? t[campoNaoLidas] || 0 : (t[campoNaoLidas] || 0) + 1,
        };
      });
      // Conversa mais recente sobe, como na ordenação do servidor.
      return [...out].sort((a, b) =>
        String(b.last_message_at || "").localeCompare(String(a.last_message_at || "")),
      );
    });
    if (!achou) {
      // Conversa nova: busca só ela (não a lista toda).
      const base = interna ? "/chat/internal/threads" : "/chat/threads";
      api
        .get(`${base}/${tid}`)
        .then(({ data }) => {
          const item = data?.id ? data : null;
          if (!item) return;
          setter((prev) => (prev.some((t) => t.id === item.id) ? prev : [item, ...prev]));
        })
        .catch(() => (interna ? fetchInternal() : fetchThreads()));
    }
  }, [fetchInternal, fetchThreads]);

  // Serviço mudou (status, protocolo): atualiza apenas as conversas daquele
  // serviço, para a lista não mostrar status velho.
  const aplicarServico = useCallback(async (payload) => {
    const sid = payload?.service_id;
    if (!sid) return;
    const alvo = (t) => t.scope === "service" && String(t.service_id) === String(sid);
    if (!threadsRef.current.some(alvo) && !internalRef.current.some(alvo)) return;
    try {
      const { data } = await api.get(`/chat/threads/service/${sid}`);
      const novos = data?.items || [];
      if (!novos.length) return;
      const merge = (prev) =>
        prev.map((t) => {
          const n = novos.find((x) => x.id === t.id);
          return n ? { ...t, ...n } : t;
        });
      setThreads(merge);
      setInternalThreads(merge);
    } catch {
      /* silencioso */
    }
  }, []);

  useEffect(() => {
    const unsubs = [
      subscribeAdminWS("chat:message", (p) => aplicarMensagem(p, false)),
      // Aviso de leitura: só zera o contador quando vier, nunca soma.
      subscribeAdminWS("chat:thread_update", (p) => {
        if (!p?.thread_id || p.unread_admin === undefined) return;
        setThreads((prev) =>
          prev.map((t) => (t.id === p.thread_id ? { ...t, unread_admin: p.unread_admin } : t)),
        );
      }),
      subscribeAdminWS("chat:internal:message", (p) => aplicarMensagem(p, true)),
      subscribeAdminWS("service:update", aplicarServico),
    ];
    return () => unsubs.forEach((u) => u());
  }, [aplicarMensagem, aplicarServico]);

  // Rede de segurança: se o WebSocket cair, a lista se corrige sozinha.
  useEffect(() => {
    const id = setInterval(() => {
      fetchThreads();
      fetchInternal();
    }, 60000);
    return () => clearInterval(id);
  }, [fetchThreads, fetchInternal]);

  // Contadores derivados da lista: assim a atualização pontual já reflete no
  // sino, sem depender de uma nova listagem.
  const totalUnread = useMemo(
    () => threads.reduce((a, t) => a + (t.unread_admin || 0), 0),
    [threads],
  );
  const internalUnread = useMemo(
    () => internalThreads.reduce((a, t) => a + (t.unread || 0), 0),
    [internalThreads],
  );
  const mentionTotal = useMemo(
    () => internalThreads.reduce((a, t) => a + (t.mention_unread || 0), 0),
    [internalThreads],
  );

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);
  useEffect(() => {
    internalRef.current = internalThreads;
  }, [internalThreads]);

  const isInternal = mode === "internal";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = isInternal ? internalThreads : threads;
    return base.filter((t) => {
      if (filter === "closed") {
        if (!isClosedThread(t)) return false;
      } else if (filter === "mentions") {
        if (!((t.mention_unread || 0) > 0)) return false;
        if (isClosedThread(t)) return false;
      } else if (filter === "unread") {
        const n = (isInternal ? t.unread : t.unread_admin) || 0;
        if (t.id !== activeId && !(n > 0 || prefs[t.id]?.unread_flag)) return false;
      } else {
        // Conversas encerradas só aparecem no filtro "Conversas encerradas".
        if (isClosedThread(t)) return false;
        if (filter !== "all" && t.scope !== filter) return false;
      }
      if (!q) return true;
      return [
        conversationTitle(t, { internal: isInternal }),
        conversationSubtitle(t, { internal: isInternal }),
        t.last_human_preview,
        t.last_message_preview,
        t.service_protocolo,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [isInternal, internalThreads, threads, filter, search, prefs, activeId]);

  // Ativos primeiro; OS finalizada/cancelada desce para "Encerrados".
  // Dentro de cada grupo, fixadas sobem para o topo.
  const sections = useMemo(() => {
    const byPinned = (a, b) =>
      Number(!!prefs[b.id]?.pinned) - Number(!!prefs[a.id]?.pinned);
    const open = filtered.filter((t) => !isClosedThread(t)).sort(byPinned);
    const closed = filtered.filter(isClosedThread).sort(byPinned);
    return [
      { id: "open", label: "Ativos", items: open },
      { id: "closed", label: "Encerrados", items: closed },
    ].filter((s) => s.items.length);
  }, [filtered, prefs]);

  const flatOrder = useMemo(
    () => sections.flatMap((s) => s.items.map((t) => t.id)),
    [sections],
  );

  // Setas navegam a lista, Esc fecha o drawer.
  const onListKeyDown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const i = flatOrder.indexOf(activeId);
    const next =
      e.key === "ArrowDown"
        ? flatOrder[Math.min(flatOrder.length - 1, i + 1)]
        : flatOrder[Math.max(0, i - 1)];
    if (next) setActiveId(next);
  };

  const active = useMemo(
    () => (isInternal ? internalThreads : threads).find((t) => t.id === activeId),
    [isInternal, internalThreads, threads, activeId],
  );

  const activePref = (active && prefs[active.id]) || {};

  // Aba Aplicativo: foto (selfie do app) do profissional nas conversas diretas.
  const directPersonIds = useMemo(() => {
    if (isInternal) return [];
    return threads
      .filter((t) => t.scope !== "service" && t.person_id)
      .map((t) => String(t.person_id));
  }, [isInternal, threads]);
  const profPhotos = useProfPhotos(directPersonIds);
  const photoFor = useCallback(
    (t) =>
      !isInternal && t && t.scope !== "service" && t.person_id
        ? profPhotos[String(t.person_id)] || null
        : null,
    [isInternal, profPhotos],
  );

  async function createThread(threadId) {
    await (isInternal ? fetchInternal() : fetchThreads());
    setActiveId(threadId);
  }

  async function toggleClosed(threadId, close) {
    const base = isInternal ? "/chat/internal/threads" : "/chat/threads";
    try {
      await api.post(`${base}/${threadId}/status`, {
        status: close ? "closed" : "active",
      });
      await (isInternal ? fetchInternal() : fetchThreads());
      toast.success(close ? "Conversa encerrada" : "Conversa reaberta");
    } catch (err) {
      toast.error("Não foi possível mudar o status da conversa", {
        description: formatApiError(err.response?.data?.detail),
      });
    }
  }

  const badge = totalUnread + internalUnread;

  return (
    <>
      <button
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        onClick={() => setOpen(true)}
        data-testid="header-chat-btn"
        aria-label="Mensagens"
      >
        <MessageCircle size={20} className="text-gray-500" />
        {badge > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] text-[10px] font-bold rounded-full bg-[#ff9945] text-white flex items-center justify-center px-1"
            data-testid="header-chat-badge"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          // z acima dos overlays do mapa do Painel Logístico (z-[1300]..z-[1400])
          className="w-full sm:w-[55rem] sm:max-w-none flex flex-col gap-0 p-0 [&>button:first-child]:hidden !z-[1700]"
          overlayClassName="!z-[1690]"
        >
          <div
            className="px-6 py-4 text-white flex items-center justify-between shrink-0"
            style={accentBg()}
          >
            <SheetTitle className="flex items-center gap-2.5 text-white">
              <MessageCircle className="h-5 w-5" />
              Mensagens
            </SheetTitle>
            <button
              className="text-white/80 hover:text-white transition-colors"
              onClick={() => setOpen(false)}
              data-testid="chat-close-btn"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex flex-1 min-h-0 flex-row">
          <aside
            className="w-full sm:w-[340px] border-r border-gray-200 flex flex-col bg-gray-50"
            data-testid="chat-thread-list"
          >

            {/* Seletor: profissionais (app) x equipe (usuários web) */}
            <div className="px-3 pt-3 bg-white">
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl p-1 w-fit">
                {[
                  { id: "mobile", label: "Aplicativo", icon: Smartphone, unread: totalUnread },
                  { id: "internal", label: "Web", icon: Users, unread: internalUnread, mention: mentionTotal > 0 },
                ].map((m) => {
                  const Icon = m.icon;
                  const on = mode === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        setMode(m.id);
                        setActiveId(null);
                        setFilter("all");
                      }}
                      aria-pressed={on}
                      className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-colors ${
                        on ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"
                      }`}
                      data-testid={`chat-mode-${m.id}`}
                    >
                      <Icon size={14} />
                      {m.label}
                      {m.mention && (
                        <span
                          className={`inline-flex items-center justify-center h-[15px] w-[15px] rounded-full ${
                            on ? "bg-white/25 text-white" : "bg-[var(--accent)] text-white"
                          }`}
                          title="Você foi mencionado"
                          data-testid="chat-mode-mention-dot"
                        >
                          <AtSign size={9} strokeWidth={3} />
                        </span>
                      )}
                      {m.unread > 0 && (
                        <span
                          className={`min-w-[16px] h-[16px] text-[10px] font-bold rounded-full flex items-center justify-center px-1 ${
                            on ? "bg-white/25 text-white" : "bg-[#FF7348] text-white"
                          }`}
                        >
                          {m.unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-3 border-b border-gray-200 bg-white space-y-2">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar conversa…"
                  className="pl-8 h-9 text-sm"
                  data-testid="chat-search-input"
                />
              </div>
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-1 w-fit">
                {[
                  { id: "all", label: "Todas as conversas", icon: LayoutList },
                  { id: "unread", label: "Conversas não lidas", icon: Mail },
                  {
                    id: "general",
                    label: isInternal ? "Conversas diretas" : "Conversas gerais",
                    icon: MessageCircle,
                  },
                  { id: "service", label: "Conversas de serviços", icon: Wrench },
                  ...(isInternal
                    ? [
                        {
                          id: "mentions",
                          label: "Onde te marcaram com @",
                          count: mentionTotal,
                          icon: AtSign,
                        },
                      ]
                    : []),
                  { id: "closed", label: "Conversas encerradas", icon: Archive },
                ].map((f) => (
                  <IconTip key={f.id} label={f.label}>
                    <button
                      type="button"
                      onClick={() => setFilter(f.id)}
                      aria-label={f.label}
                      aria-pressed={filter === f.id}
                      className={`h-7 px-2.5 rounded-lg transition-colors inline-flex items-center gap-1 ${
                        filter === f.id
                          ? "bg-gray-100 text-gray-900"
                          : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                      }`}
                      data-testid={`chat-filter-${f.id}`}
                    >
                      <f.icon size={13} strokeWidth={2.5} />
                      {f.count > 0 && (
                        <span
                          className="min-w-[14px] h-[14px] text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 bg-[#FF7348] text-white"
                        >
                          {f.count > 9 ? "9+" : f.count}
                        </span>
                      )}
                    </button>
                  </IconTip>
                ))}
                </div>
                <IconTip label="Nova conversa">
                  <button
                    onClick={() => setNewOpen(true)}
                    aria-label="Nova conversa"
                    className="ml-auto h-7 w-7 shrink-0 rounded-full bg-[var(--accent)] text-white inline-flex items-center justify-center hover:opacity-90 transition-opacity"
                    data-testid="chat-new-internal-btn"
                  >
                    <Plus size={14} strokeWidth={3} />
                  </button>
                </IconTip>
              </div>
            </div>

            <div
              ref={listRef}
              tabIndex={0}
              onKeyDown={onListKeyDown}
              className="flex-1 overflow-y-auto outline-none"
              data-testid="chat-thread-scroll"
            >
              {sections.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-12 px-6">
                  <Inbox size={32} className="mx-auto mb-2 opacity-30" />
                  {filter === "unread"
                    ? "Nenhuma conversa não lida."
                    : filter === "mentions"
                    ? "Nenhuma menção pendente. Quando alguém te marcar com @, aparece aqui."
                    : filter === "closed"
                      ? "Nenhuma conversa encerrada."
                      : isInternal
                        ? "Nenhuma conversa com a equipe ainda. Clique em +."
                        : "Nenhuma conversa encontrada."}
                </div>
              ) : (
                sections.map((sec) => (
                  <div key={sec.id}>
                    <div
                      className="sticky top-0 z-[1] px-3 py-1.5 bg-gray-50/90 backdrop-blur text-[10px] font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-100"
                      data-testid={`chat-section-${sec.id}`}
                    >
                      {sec.label} · {sec.items.length}
                    </div>
                    {sec.items.map((t) => (
                      <ConversationItem
                        key={t.id}
                        thread={t}
                        internal={isInternal}
                        photoUrl={photoFor(t)}
                        active={t.id === activeId}
                        onClick={() => setActiveId(t.id)}
                        pref={prefs[t.id] || {}}
                        onTogglePin={(id, pinned) => setPref(id, { pinned })}
                        onToggleMute={(id, muted) => setPref(id, { muted })}
                        onMarkUnread={(id) => {
                          if (activeId === id) setActiveId(null);
                          setPref(id, { unread_flag: true });
                        }}
                        onToggleClosed={toggleClosed}
                        onEditGroup={(id) => {
                          setActiveId(id);
                          setEditGroupId(id);
                        }}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>
          </aside>

          <div className="flex-1 flex flex-col bg-white min-w-0">
            {active ? (
              <>
                <div className="px-4 py-3 border-b border-gray-200 bg-white/85 backdrop-blur sticky top-0 z-10">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const custom = active.is_group && active.icon;
                      const HeaderIcon = custom
                        ? groupIcon(active.icon)
                        : threadIcon(active, isInternal);
                      const c = active.is_group
                        ? groupColors(active.color)
                        : groupColors("accent");
                      return (
                        <ThreadAvatar
                          key={photoFor(active) || "icon"}
                          photoUrl={photoFor(active)}
                          icon={HeaderIcon}
                          colors={c}
                          size="h-9 w-9"
                          iconSize={16}
                          testId="chat-header-photo"
                        />
                      );
                    })()}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {activePref.pinned && <Pin size={11} className="text-gray-400" />}
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {conversationTitle(active, { internal: isInternal })}
                        </span>
                        {active.service_status && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-600 border-gray-200"
                            data-testid="chat-header-status"
                          >
                            {active.service_status === "cancelado"
                              ? "Cancelado"
                              : (STATUS_OPERACIONAL_MAP[active.service_status] || {}).label ||
                                active.service_status}
                          </span>
                        )}
                        {activePref.muted && <BellOff size={11} className="text-gray-400" />}
                        {active.status === "archived" && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-500 border-gray-200 inline-flex items-center gap-1"
                            data-testid="chat-header-archived"
                          >
                            <Archive size={9} />
                            Arquivado
                          </span>
                        )}
                        {active.status === "closed" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-gray-100 text-gray-500 border-gray-200 inline-flex items-center gap-1">
                            <Archive size={9} />
                            Encerrada
                          </span>
                        )}
                      </div>
                      <IconTip
                        label={
                          active.scope === "service" && active.service_type_name
                            ? "Tipo de serviço"
                            : isInternal
                              ? "Pessoas nesta conversa"
                              : "Profissional envolvido no acionamento"
                        }
                        side="bottom"
                      >
                        <p
                          className="text-[11px] text-gray-500 truncate mt-0.5 w-fit max-w-full"
                          data-testid="chat-header-subtitle"
                        >
                          {active.scope === "service" && active.service_type_name
                            ? active.service_type_name
                            : conversationSubtitle(active, { internal: isInternal })}
                        </p>
                      </IconTip>
                    </div>

                    <div className="flex items-center gap-0.5 shrink-0">
                      <IconTip
                        label={activePref.pinned ? "Desafixar conversa" : "Fixar no topo"}
                      >
                        <button
                          onClick={() => setPref(active.id, { pinned: !activePref.pinned })}
                          className={`p-1.5 rounded hover:bg-gray-100 ${
                            activePref.pinned ? "text-[var(--accent)]" : "text-gray-400"
                          }`}
                          data-testid="chat-header-pin"
                        >
                          {activePref.pinned ? <PinOff size={15} /> : <Pin size={15} />}
                        </button>
                      </IconTip>
                      <IconTip
                        label={activePref.muted ? "Reativar avisos" : "Silenciar conversa"}
                      >
                        <button
                          onClick={() => setPref(active.id, { muted: !activePref.muted })}
                          className={`p-1.5 rounded hover:bg-gray-100 ${
                            activePref.muted ? "text-[var(--accent)]" : "text-gray-400"
                          }`}
                          data-testid="chat-header-mute"
                        >
                          {activePref.muted ? <Bell size={15} /> : <BellOff size={15} />}
                        </button>
                      </IconTip>
                      <IconTip label="Marcar como não lida">
                        <button
                          onClick={() => {
                            setPref(active.id, { unread_flag: true });
                            setActiveId(null);
                          }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                          data-testid="chat-header-unread"
                        >
                          <MailOpen size={15} />
                        </button>
                      </IconTip>
                      {active.status !== "archived" &&
                        (isInternal || active.scope === "service" || active.status === "closed") && (
                        <IconTip
                          label={
                            active.status === "closed" ? "Reabrir conversa" : "Encerrar conversa"
                          }
                        >
                          <button
                            onClick={() => toggleClosed(active.id, active.status !== "closed")}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                            data-testid="chat-header-close-thread"
                          >
                            {active.status === "closed" ? (
                              <ArchiveRestore size={15} />
                            ) : (
                              <Archive size={15} />
                            )}
                          </button>
                        </IconTip>
                      )}
                      {active.service_id && (
                        <IconTip label="Abrir o serviço em outra janela">
                          <button
                            onClick={() =>
                              window.open(
                                `/services/list?edit=${active.service_id}`,
                                "_blank",
                                "noopener",
                              )
                            }
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                            data-testid="chat-open-service-btn"
                          >
                            <ExternalLink size={15} />
                          </button>
                        </IconTip>
                      )}
                      <span className="w-px h-5 bg-gray-200 mx-1" />
                      <IconTip label="Fechar conversa">
                        <button
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                          onClick={() => setActiveId(null)}
                          data-testid="chat-drawer-close"
                        >
                          <X size={16} />
                        </button>
                      </IconTip>
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <ChatPanel
                    threadId={active.id}
                    currentUserId={user?.id}
                    apiBase={isInternal ? "/chat/internal/threads" : "/chat/threads"}
                    wsEvent={isInternal ? "chat:internal:message" : "chat:message"}
                    mentionUsers={isInternal ? webUsers : null}
                    currentUserName={user?.name || user?.nome || ""}
                    quickMessagesServiceId={!isInternal ? active.service_id : null}
                    readonly={
                      active.status === "closed" ||
                      active.status === "archived" ||
                      active.can_send === false
                    }
                    readonlyHint={
                      active.status === "archived"
                        ? "Grupo arquivado: ninguém mais participa. Adicione alguém em “Editar grupo” para reativar."
                        : active.can_send === false
                          ? "Você foi removido deste grupo e agora só pode ler o histórico."
                          : `Conversa encerrada${
                              active.closed_by_name ? ` por ${active.closed_by_name}` : ""
                            }. Use o menu ⋯ da conversa para reabrir.`
                    }
                    onRead={() => {
                      (isInternal ? fetchInternal : fetchThreads)();
                      setPrefs((prev) =>
                        prev[active.id]?.unread_flag
                          ? { ...prev, [active.id]: { ...prev[active.id], unread_flag: false } }
                          : prev,
                      );
                    }}
                  />
                </div>
              </>
            ) : (
              <div
                className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8"
                data-testid="chat-empty-state"
              >
                <span
                  className="h-16 w-16 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "var(--accent-light)" }}
                >
                  <MessageCircle size={28} style={{ color: "var(--accent)" }} />
                </span>
                <p className="text-sm font-medium text-gray-500">
                  Nenhuma conversa selecionada
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Escolha uma conversa ao lado — use as setas ↑ ↓ para navegar.
                </p>
              </div>
            )}
          </div>
          </div>
        </SheetContent>
      </Sheet>

      <NewConversationModal
        open={newOpen}
        onOpenChange={setNewOpen}
        mode={mode}
        webUsers={webUsers}
        mobileUsers={mobileUsers}
        onCreated={createThread}
      />

      <EditGroupDrawer
        open={!!editGroupId}
        onClose={() => setEditGroupId(null)}
        thread={internalThreads.find((t) => t.id === editGroupId) || null}
        webUsers={webUsers}
        meId={user?.id}
        onChanged={fetchInternal}
      />
    </>
  );
}
